/**
 * Component: ShopTheLook
 * ------------------------------------------------------------------------------
 * Modal component with products
 *
 * @namespace ShopTheLook
 */
import { getInternationalSizing } from '~/format/utils'
import { getItemProperties, isComingSoonProduct } from '~/helpers/product'
import { stringToHandle } from '~/helpers/strings'

/**
 * Selectors.
 */
const selectors = {
  backInStockButton: '[js-shop-the-look="backInStock"]',
  lengthContainer: '[js-shop-the-look="lengthContainer"]',
  lengthInput: '[js-shop-the-look="lengthInput"]',
  popupTrigger: '[data-open-popup]',
  sizeOptionLabel: '[js-shop-the-look="sizeOptionLabel"]',
}

const ShopTheLook = (() => {
  let shopTheLookPopups = false
  let productForms = false
  let shopTheLookContents = false

  let nodeSelectors = {}

  const showCartAddedPopup = (addedBlock) => {
    if ( addedBlock && (addedBlock instanceof Element || addedBlock instanceof HTMLDocument) ) {
      addedBlock.classList.add('visible')
      setTimeout(() => {
        addedBlock.classList.remove('visible')
      }, 3000)
    }
  }

  const getAvailableOptions = (selectedOptions, product) => {
    const options = product.options_with_values.map(option => `option${option.position}`)
    const availableOptions = {}

    const variants = product.variants.filter((variant) => variant.available)

    for (const key of options) {
      let filteringVariants = [...variants]

      availableOptions[key] = []

      for (const filteringKey in selectedOptions) {
        if (key === filteringKey) continue

        filteringVariants = filteringVariants.filter(
          (varinat) => varinat[filteringKey] === selectedOptions[filteringKey],
        )
      }

      filteringVariants.forEach((variant) => {
        const value = variant[key]
        if (availableOptions[key].includes(value)) return

        availableOptions[key].push(value)
      })
    }

    return availableOptions

  }

  const getLowStockOptions = (product, form) => {
    const options = {}
    product.options_with_values.forEach(option => {
      options[`option${option.position}`] = option.values
    })
    const variants = product.variants.filter((variant) => variant.available)

    const lowStockOptions = []

    for (const key in options) {
      for (const value of options[key]) {
        const filteredVariants = variants.filter(variant => {
          return variant[key] === value
        })
        let total = 0
        filteredVariants.forEach((variant) => {
          total += variant.inventory_quantity
        })
        if (total > 0 && total < Number(form.dataset.lowStock)) {
          lowStockOptions.push(value)
        }
      }
    }

    return lowStockOptions
  }

  const handleOptionChange = (form, product, optionsLabels, changedOption) => {
    const formData = new FormData(form)
    const selectedOptions = {}

    if (!product) {
      return
    }

    for (const entry of formData.entries()) {
      const [key, value] = entry
      if (!key.includes('option')) continue
      selectedOptions[key] = value

      const currentOptions = key === 'option1' ? optionsLabels : optionsLabels.filter(label => label.classList.contains(key))
      currentOptions.forEach(label => {
        if (label.dataset.value === value) {
          label.classList.add('selected')
        } else {
          label.classList.remove('selected')
        }
      })
    }

    for (const optionName in selectedOptions) {
      const value = selectedOptions[optionName]
      const currentOptions = optionsLabels.filter(label => label.classList.contains(optionName))
      currentOptions.forEach(label => {
        if (label.dataset.value === value) {
          label.classList.add('selected')
        } else {
          label.classList.remove('selected')
        }
      })
    }

    if (Object.keys(selectedOptions).length == product.options.length) {
      const activeVariant = product.variants.find(variant => {
        let isSelectedVariant = true

        for (const option in selectedOptions) {
          if (variant[option] !== selectedOptions[option]) isSelectedVariant = false
        }
        return isSelectedVariant
      })

      if (activeVariant?.available) {
        const variantTitle = getInternationalTitle(product, activeVariant)
        form.dataset.variantId = activeVariant.id
        form.querySelector('.stl-variants-selector__close')?.click()
        form.querySelector('.stl-variants-selector__btn').innerText = variantTitle
      } else {
        form.dataset.variantId = ''
        form.querySelector('.stl-variants-selector__btn').innerText = form.dataset.selectText

      }
    }

    const availableOptions = getAvailableOptions(selectedOptions, product)
    const lowStockOptions = getLowStockOptions(product, form)

    for (const optionName in availableOptions) {
      const currentOptions = optionsLabels.filter(label => label.classList.contains(optionName))
      currentOptions.forEach(label => {
        if (availableOptions[optionName].includes(label.dataset.value)) {
          label.classList.remove('disabled')
        } else {
          label.classList.add('disabled')
        }
      })
    }
    optionsLabels.forEach(label => {
      if (lowStockOptions.includes(label.dataset.value)) {
        label.classList.add('low-stock')
      } else {
        label.classList.remove('low-stock')
      }
    })
  }

  /**
   * Pre-select the "Regular" length option, if it exists.
   * @param {Element} variantSelector - A variant selector wrapper element.
   */
  const preselectLength = (variantSelector) => {
    const lengthOption = variantSelector?.querySelector('[js-shop-the-look="lengthInput"]')

    const selectedLengthOption = variantSelector?.querySelector('[js-shop-the-look="lengthInput"]:checked')

    if (!variantSelector || !lengthOption || selectedLengthOption) {
      return
    }

    const regularLength = variantSelector.querySelector('[value="Regular"]')
    regularLength.click()
  }

  const eventListeners = () => {
    shopTheLookPopups.forEach(popup => {
      popup.addEventListener('click', (_event) => {
        const openedVariantSelector = popup.querySelector('.stl-variants-selector.visible')
        const openSelector = _event.target.closest('.stl-variants-selector__btn')
        const currentVariantSelector = _event.target.closest('.stl-variants-selector')
        const closeSelector = _event.target.closest('.stl-variants-selector__close')

        const wishlist = _event.target.closest('[data-swaction="addToWatchlist"]')

        if (wishlist) {
          wishlist.click()
        }

        if (openSelector) {
          openedVariantSelector?.classList.remove('visible')
          currentVariantSelector?.classList.add('visible')
          preselectLength(currentVariantSelector)
        }

        if (closeSelector || !currentVariantSelector) {
          openedVariantSelector?.classList.remove('visible')
        }
      })
    })

    productForms.forEach(form => {
      const backInStockButtons = [...form.querySelectorAll(selectors.backInStockButton)]
      const optionsLabels = [...form.querySelectorAll('.stl-option-label')]
      const sizeOptionLabels = [...form.querySelectorAll(selectors.sizeOptionLabel)]

      /**
       * Component renders JSON regardless of if a product exists,
       */
      let product = JSON.parse(form.querySelector('[data-product-json]').innerHTML)

      if (!Object.keys(product).length) {
        product = false
      }

      if (!product) {
        return
      }

      /* Define whether the product is `coming soon` */
      product.comingSoon = isComingSoonProduct(product.tags)

      if (mint.flags?.international?.showLocalisedSizes) {
        insertInternationalSizes(product, sizeOptionLabels)
      }

      handleOptionChange(form, product, optionsLabels, 'option1')

      form.addEventListener('change', (_event) => {
        const changedOption = _event.target.dataset.option
        handleOptionChange(form, product, optionsLabels, changedOption)
      })

      form.addEventListener('submit', (_event) => {
        _event.preventDefault()

        form.querySelector('.stl-variants-selector__close')?.click()

        if (form.dataset.variantId) {
          const variantId = form.dataset.variantId
          const matchingVariant = product.variants.find((variant) => {
            return Number(variant.id) === Number(variantId)
          })

          const { formattedProduct, formattedVariant } = addRequiredFields(product, matchingVariant)
          const properties = getItemProperties(formattedProduct, formattedVariant)

          const formData = {
            id: variantId,
            quantity: 1,
            properties: properties,
          }

          const formNamespace = form.getAttribute('data-stl-form')

          mint.EventBus.dispatch('cart:addItems', {
            data: formData,
            formNamespace,
            preventDrawer: true,
          })

        } else {
          form.querySelector('.select-box__btn.stl-variants-selector__btn')?.click()
        }
      })

      /**
       * User clicks on a 'Notify me' button.
       */
      backInStockButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault()
          initNotifyMePopup(product, event)
        })
      })
    })

    mint.EventBus.on('cart:added', (response) => {
      const requestForm = document.querySelector(
        `[data-stl-form="${response.request?.formNamespace}"]
      `)

      if (!requestForm) {
        return
      }

      /**
       * Locate the Product Card that holds the Variant Selector.
       * - DOM Traversal not ideal
       */
      const productCard = requestForm.closest('[data-product-card]')
      const shopTheLookId = requestForm.closest('[data-stl-id]')?.dataset.stlId

      if (!productCard) {
        return
      }

      const productSuccessfullyAddedBlock = productCard.querySelector(`[data-product-added="${response.response.handle}"]`)
      const productErrorMessageBlock = productCard.querySelector(`[data-product-error="${response.response.handle}"]`)

      productErrorMessageBlock?.classList.add('product-form__error-message-wrapper--hidden')
      showCartAddedPopup(productSuccessfullyAddedBlock)

      gtag('event', 'add_to_cart', {
        items: [
          {
            item_list_id: shopTheLookId,
            item_list_name: 'shop the look',
          },
        ],
      })
    })

    shopTheLookContents.forEach(el => {
      const shopTheLookId = el.dataset.stlId || ''
      el.addEventListener( 'click', (_event) => {
        gtag('event', 'select_content', {
          content_type: 'Shop_The_Look_Overlay',
          item_id: shopTheLookId,
        })
      })
    })
  }

  /**
   * Insert 'International Sizes' into the 'Sizes' select box.
   * @param {Object} product - Shopify product JSON.
   * @param {Array} sizeOptionLabels - Array of `option` HTMLElements
   * for a given 'Size' select box.
   */
  const insertInternationalSizes = (product, sizeOptionLabels) => {
    for (const label of sizeOptionLabels) {

      /**
       * Value is assigned to data attribute so we don't have to trim/strip `innerHTML`.
       */
      const sizeValue = label.getAttribute('js-size-option-value')
      const equivalentSize = getInternationalSizing(sizeValue, product.international_sizes)

      if (!equivalentSize) {
        continue
      }

      label.setAttribute('js-international-size-value', equivalentSize)

      label.innerText = equivalentSize
    }
  }

  /**
   * Open the 'Add to Watch list' button.
   * @param {Object} product - Shopify product JSON.
   * @param {Event} event - Click event that triggered Watchlist.
   */
  const initNotifyMePopup = (product, event) => {
    const targetButton = event.target.closest('button')
    const optionName = targetButton.getAttribute('data-option-name')
    const selectedOptions = [optionName]

    /**
     * Locate International equivalent in order to pass up to Back in Stock
     * - Ideally we'd only loop over this once in the variant selector and
     *   set the value somewhere else, but would require a bit of a rework
     * - Will return false if nothing and BIS will display default values.
     */
    const equivalentSize = getInternationalSizing(optionName, product.international_sizes)

    /**
     * Get 'Length' value if exists.
     * - DOM traversal not ideal, wider component re-work preferred.
     */
    const parentForm = targetButton.closest('form')
    const lengthContainer = parentForm.querySelector(selectors.lengthContainer)
    const radio = lengthContainer.querySelector(selectors.lengthInput)

    if (radio) {
      const radio = lengthContainer.querySelector(selectors.lengthInput)
      const radioName = radio.name

      const checkedLength = lengthContainer.querySelector(`input[name="${radioName}"]:checked`)?.value

      if (checkedLength) {
        selectedOptions.push(checkedLength)
      }
    }

    /**
     * Match the variant using the options.
     */
    const variant = product.variants.find((variant) => {
      return variant.options.every((option) => {
        return selectedOptions.includes(option)
      })
    })

    mint.EventBus.dispatch('wishlist:addToWatchlist', {
      product,
      variant,
      event,
      sizeValue: equivalentSize,
    })
  }

  /**
   * Initialise component.
   */
  const init = () => {
    shopTheLookPopups = document.querySelectorAll('[js-shop-the-look-popup]')
    productForms = document.querySelectorAll('.stl-variants-selector__form')
    shopTheLookContents = document.querySelectorAll('[data-gtag-select-content]')

    nodeSelectors = {
      popupTrigger: [...document.querySelectorAll(selectors.popupTrigger)],
    }

    if (!nodeSelectors.popupTrigger.length) {
      return
    }

    eventListeners()
  }

  /**
   * Adds required fields to the product and variant objects.
   * - This allows to use them further in some global helper functions
   * - Currently updates only to set the correct item properties when adding to cart
   *
   * @param {Object} product - The product object (from product-json.liquid snippet).
   * @param {Object} variant - The variant object.
   *
   * @returns {Object} An object containing the `formattedProduct` and `formattedVariant` objects.
   */
  function addRequiredFields(product, variant) {
    if (!product || !variant) {
      return { product, variant }
    }

    const selectedOptions = []
    const formattedProduct = { ...product }
    const formattedVariant = { ...variant }
    const { formattedOptions, usesInternationalSizing } = getFormattedOptions(product)

    variant.options.forEach((optionValue, index) => {
      const optionName = formattedOptions[index].name
      let equivalentValue = optionValue

      if (stringToHandle(optionName) === 'size') {
        const equivalentSize = getInternationalSizing(optionValue, product.international_sizes)
        equivalentValue = equivalentSize
      }

      selectedOptions.push({ name: optionName, value: equivalentValue })
    })

    formattedProduct.options = formattedOptions
    formattedProduct.usesInternationalSizing = usesInternationalSizing
    formattedVariant.selectedOptions = selectedOptions

    return { formattedProduct, formattedVariant }
  }

  /**
   * Return options and variable indicating if international sizes are enabled.
   *
   * * Note: almost similar to the src/js/format/product.js method but with some differences
   * due to the current product structure
   *
   * @param {Object} product - Unformatted Product JSON from product-json.liquid snippet.
   * @returns {Object}
   */
  function getFormattedOptions(product) {
    let usesInternationalSizing = false

    const formattedOptions = product.options_with_values.map((option, index) => {
      return {
        index,
        name: option.name,
        values: option.values,
      }
    })

    /**
     * International sizing check for each of the product's options.
     * - Only runs if an option's name contains `size`.
     */
    const internationalSizes = getProductIntlSizes(product)

    if (internationalSizes) {
      for (const option of formattedOptions) {
        const isSizingOption = stringToHandle(option.name) === 'size'

        if (!isSizingOption) {
          continue
        }

        const intlValues = option.values.map(optionValue => {
          const match = getInternationalSizing(optionValue, internationalSizes)

          if (!match) {
            return false
          }

          return match
        }).filter(Boolean)

        if (intlValues.length === option.values.length) {
          option.values = intlValues
          usesInternationalSizing = true
        }
      }
    }

    return { formattedOptions, usesInternationalSizing }
  }

  /**
   * Return a parsed JSON object of the product's international sizing metafield.
   * - If the metafield does not exist, return false.
   * - If the feature flag for localized sizes is disabled, return false.
   *
   * * Note: almost similar to the src/js/format/product.js method but with some differences
   * due to the current product structure
   *
   * @param {Object} product - Unformatted Product JSON from product-json.liquid snippet.
   * @returns {Boolean|Object}
   */
  function getProductIntlSizes(product) {
    const featureFlag = mint.flags?.international?.showLocalisedSizes
    const sizes = product?.international_sizes

    if (!featureFlag || !sizes) {
      return false
    }

    return sizes
  }

  /**
   * Return a formatted title for products which use international sizes.
   *
   * @param {Object} product - Unformatted Product JSON from product-json.liquid snippet.
   * @param {Object} variant - The variant object.
   *
   * @returns {String}
   */
  function getInternationalTitle(product, variant) {
    const featureFlag = mint.flags?.international?.showLocalisedSizes
    const sizes = product?.international_sizes
    let variantTitle = variant?.title

    if (!featureFlag || !sizes) {
      return variantTitle
    }

    const sizeOptionIndex = product?.options.findIndex(option => stringToHandle(option) === 'size')
    const sizeOptionValue = variant?.options[sizeOptionIndex]
    const equivalentSize = getInternationalSizing(sizeOptionValue, product?.international_sizes)

    if (equivalentSize) {
      const variantOptions = [ ...variant.options ]
      variantOptions[sizeOptionIndex] = equivalentSize
      variantTitle = variantOptions.join(' / ')
    }

    return variantTitle
  }

  /**
   * Expose public interface.
   */
  return {
    init,
  }
})()

export default ShopTheLook
